import { Injectable } from '@angular/core';

// Any settings service can plug into the seed mechanism by registering itself here — the
// generate/apply logic is generic and never needs to change when settings are added or a
// new settings service is introduced.
export interface SettingsSeedSource<T = unknown> {
	/** Unique key identifying this settings source inside the seed payload. */
	key: string;
	getState: () => T;
	applyState: (state: T) => void;
}

interface SettingsSeedPayload {
	v: number;
	sources: Record<string, unknown>;
}

const SEED_VERSION = 1;

/**
 * Encodes/decodes a debug "seed" string that captures the current state of all registered
 * settings sources, so a support engineer can restore an end user's exact settings locally.
 */
@Injectable({
	providedIn: 'root'
})
export class SettingsSeedService {
	private sources = new Map<string, SettingsSeedSource>();

	register<T>(source: SettingsSeedSource<T>): void {
		this.sources.set(source.key, source as SettingsSeedSource);
	}

	generateSeed(): string {
		const payload: SettingsSeedPayload = { v: SEED_VERSION, sources: {} };
		for (const [key, source] of this.sources) {
			payload.sources[key] = source.getState();
		}
		return btoa(unescape(encodeURIComponent(JSON.stringify(payload))));
	}

	/** Applies a seed produced by `generateSeed()`. Returns false if the seed is invalid/unreadable. */
	applySeed(seed: string): boolean {
		let payload: SettingsSeedPayload;
		try {
			payload = JSON.parse(decodeURIComponent(escape(atob(seed.trim()))));
		} catch {
			return false;
		}
		if (!payload || typeof payload !== 'object' || typeof payload.sources !== 'object') return false;

		for (const [key, state] of Object.entries(payload.sources)) {
			this.sources.get(key)?.applyState(state);
		}
		return true;
	}
}
